/** The application shell: fixed dark navigation rail, top bar, content on warm canvas. */

import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import {
  BarChart3,
  Bug,
  ChevronLeft,
  ClipboardList,
  FileText,
  KeyRound,
  Layers,
  Layers3,
  LayoutGrid,
  Link2,
  ListOrdered,
  LogOut,
  Map,
  MessageSquareWarning,
  Moon,
  Settings,
  Sun,
  Target,
  Users,
} from 'lucide-react'

import { useFeedback } from '@/features/feedback/useFeedback'
import { isProjectWindow } from '@/features/projects/openProjectWindow'
import { msal } from '@/lib/auth'
import { Permissions, useSession, usePermission } from '@/lib/session'

type Theme = 'light' | 'dark'

const THEME_KEY = 'wheelerpmt.theme'

function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(THEME_KEY)
    if (stored === 'light' || stored === 'dark') return stored
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem(THEME_KEY, theme)
  }, [theme])

  return [theme, () => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))]
}

const NAV = [
  { to: '/projects', label: 'Projects', icon: LayoutGrid, permission: Permissions.projectsView },
  // Under Projects, because a WIG is a way of running a project, not a separate thing.
  { to: '/wig', label: 'WIG', icon: Target, permission: Permissions.projectsView },
  // Groups of projects with one combined status (Ben Wood's request, 2026-10-07).
  { to: '/initiatives', label: 'Initiatives', icon: Layers3, permission: Permissions.projectsView },
  // Beside Projects rather than buried in Administration: it explains how the portfolio is
  // organised, and a Portfolio Manager who cannot find it will not maintain it.
  { to: '/teams', label: 'Teams', icon: Layers, permission: Permissions.projectsView },
  {
    to: '/priorities',
    label: 'Priorities',
    icon: ListOrdered,
    permission: Permissions.prioritiesView,
  },
  {
    to: '/workbench',
    label: 'Workbench',
    icon: ClipboardList,
    permission: Permissions.workView,
  },
  {
    to: '/workload',
    label: 'Workload',
    icon: Users,
    permission: Permissions.projectsView,
  },
  {
    to: '/documents',
    label: 'Documents',
    icon: FileText,
    permission: Permissions.documentsView,
  },
  {
    to: '/roadmap',
    label: 'Roadmap',
    icon: Map,
    permission: Permissions.reportsView,
  },
  {
    to: '/status-report',
    label: 'Status report',
    icon: BarChart3,
    permission: Permissions.reportsView,
  },
  {
    to: '/shares',
    label: 'Share links',
    icon: Link2,
    permission: Permissions.reportsShare,
  },
] as const

function NavItem({
  to,
  label,
  icon: Icon,
  collapsed,
  badge,
}: {
  to: string
  label: string
  icon: typeof LayoutGrid
  collapsed: boolean
  badge?: number
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition',
          isActive ? 'bg-white/5 font-semibold' : 'hover:bg-white/5',
        ].join(' ')
      }
      style={({ isActive }) => ({ color: isActive ? 'var(--brand)' : 'var(--sidebar-fg)' })}
      title={collapsed ? label : undefined}
    >
      {({ isActive }) => (
        <>
          {/* The yellow left indicator on the active item. */}
          {isActive ? (
            <span
              className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r"
              style={{ backgroundColor: 'var(--brand)' }}
            />
          ) : null}
          <Icon size={18} aria-hidden />
          {!collapsed && <span>{label}</span>}
          {badge ? (
            <span
              className="ml-auto rounded-pill px-1.5 py-0.5 text-[10px] font-bold"
              style={{ backgroundColor: 'var(--brand)', color: 'var(--brand-on)' }}
              aria-label={`${badge} open`}
            >
              {badge}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  )
}

export function Shell() {
  const [theme, toggleTheme] = useTheme()
  const [collapsed, setCollapsed] = useState(false)
  const { data: session } = useSession()
  const canAdminister = usePermission(Permissions.adminAccess)
  // API Access is for everyone who can see the portfolio: keys act as their owner, so a
  // Viewer's key can only read (CAB parity, 2026-10-29).
  const canReadApiAccess = usePermission(Permissions.projectsView)
  const canSeeFeedback = usePermission(Permissions.feedbackSubmit)
  // The badge is why people notice a bug is already filed before filing it again.
  const { data: feedback } = useFeedback()

  const initials = (session?.displayName ?? '?')
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  // A project opened in its own window is just the project: the navigation lives in the
  // window it was opened from.
  if (isProjectWindow()) {
    return (
      <main className="h-full overflow-auto px-6 pb-12 pt-4">
        <Outlet />
      </main>
    )
  }

  return (
    <div className="flex h-full">
      {/* print:hidden — a printout is the page's content, not the app's chrome. */}
      <nav
        className="flex shrink-0 flex-col justify-between transition-all print:hidden"
        style={{
          width: collapsed ? 72 : 260,
          backgroundColor: 'var(--sidebar-bg)',
          color: 'var(--sidebar-fg)',
        }}
        aria-label="Main"
      >
        <div className="p-3">
          <div className="mb-6 flex items-center gap-3 px-1">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-heading text-xl font-bold"
              style={{ backgroundColor: 'var(--brand)', color: 'var(--brand-on)' }}
            >
              W
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="truncate font-heading text-sm font-bold uppercase">Wheeler PMT</div>
                <div
                  className="micro-label truncate"
                  style={{ color: 'var(--sidebar-muted)' }}
                >
                  IT Operations
                </div>
              </div>
            )}
          </div>

          {!collapsed && (
            <div className="micro-label mb-2 px-3" style={{ color: 'var(--sidebar-muted)' }}>
              — Plan &amp; Track
            </div>
          )}
          <div className="space-y-1">
            {NAV.map((item) => (
              <NavItem key={item.to} {...item} collapsed={collapsed} />
            ))}
          </div>

          {(canAdminister || canReadApiAccess || canSeeFeedback) && (
            <>
              {!collapsed && (
                <div
                  className="micro-label mb-2 mt-6 px-3"
                  style={{ color: 'var(--sidebar-muted)' }}
                >
                  — Platform
                </div>
              )}
              <div className="mt-1 space-y-1">
                {canSeeFeedback && (
                  <NavItem
                    to="/feedback"
                    label="Bugs &amp; requests"
                    icon={Bug}
                    collapsed={collapsed}
                    badge={feedback?.openCount}
                  />
                )}
                {canReadApiAccess && (
                  <NavItem to="/api-access" label="API access" icon={KeyRound} collapsed={collapsed} />
                )}
                {canAdminister && (
                  <NavItem
                    to="/api-feedback"
                    label="API feedback"
                    icon={MessageSquareWarning}
                    collapsed={collapsed}
                  />
                )}
                {canAdminister && (
                  <NavItem
                    to="/administration"
                    label="Administration"
                    icon={Settings}
                    collapsed={collapsed}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <div className="border-t p-3" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <div className="flex items-center gap-3 px-1">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold"
              style={{ backgroundColor: 'var(--brand)', color: 'var(--brand-on)' }}
            >
              {initials}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{session?.displayName}</div>
                <div className="truncate text-xs" style={{ color: 'var(--sidebar-muted)' }}>
                  {session?.email}
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            className="mt-3 flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-xs hover:bg-white/5"
            style={{ color: 'var(--sidebar-muted)' }}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            <ChevronLeft
              size={14}
              style={{ transform: collapsed ? 'rotate(180deg)' : undefined }}
              aria-hidden
            />
            {!collapsed && 'Collapse'}
          </button>
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-end gap-1 px-6 py-3 print:hidden">
          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-md p-2 text-fg-muted hover:bg-surface"
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            type="button"
            onClick={() => void msal.logoutRedirect()}
            className="rounded-md p-2 text-fg-muted hover:bg-surface"
            aria-label="Sign out"
          >
            <LogOut size={18} />
          </button>
        </header>

        <main className="min-w-0 flex-1 overflow-auto px-8 pb-12">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
